import { useEffect, useMemo, useState } from "react";
import { usePumpSizing } from "../../context/PumpSizingContext";
import { SERIES_TYPES } from "../../data/chart";
import { SeriesType } from "../../types/types";
import { parseNumericValue } from "../../utils/helpers/helpers";
import { getSeriesType } from "../../utils/helpers/chartHelpers";
import { getGpmDisplayValue } from "../../utils/helpers/gpmHelpers";
import { getTdhDisplayValue } from "../../utils/helpers/tdhHelpers";
import { Alert } from "../shared/Alert";
import { LoadingIndicator } from "../shared/LoadingIndicator";
import { PumpCurveChart } from "./pumpcurve/PumpCurveChart";
import { SeriesSelector } from "./pumpcurve/SeriesSelector";
import { SeriesTypeToggle } from "./pumpcurve/SeriesTypeToggle";

export function StepPumpCurve() {
	const {
		formData,
		chartData,
		isResultsLoading,
		resultsError,
		loadResults,
		hiddenSeries,
	} = usePumpSizing();
	const gpmDisplayValue = getGpmDisplayValue(formData);
	const tdhDisplayValue = getTdhDisplayValue(formData);
	const [seriesType, setSeriesType] = useState<SeriesType>("sewage");

	// Sewage and grinder pumps come back in one response for this application
	// type only, so they are split into two views here.
	const isSplitBySeriesType = formData.applicationType === "sewage";

	const availableSeriesTypes = useMemo(() => {
		const types = new Set<SeriesType>();

		for (const item of chartData?.series ?? []) {
			types.add(getSeriesType(item));
		}

		return types;
	}, [chartData]);

	const activeSeriesType = useMemo(() => {
		if (availableSeriesTypes.size === 0 || availableSeriesTypes.has(seriesType)) {
			return seriesType;
		}

		return (
			SERIES_TYPES.find((type) => availableSeriesTypes.has(type)) ?? seriesType
		);
	}, [availableSeriesTypes, seriesType]);

	const visibleSeries = useMemo(() => {
		const series = chartData?.series ?? [];

		if (!isSplitBySeriesType) {
			return series;
		}

		return series.filter((item) => getSeriesType(item) === activeSeriesType);
	}, [chartData, isSplitBySeriesType, activeSeriesType]);

	const systemCurvePoints = useMemo(
		() => chartData?.system_curve_points ?? [],
		[chartData],
	);

	const hasContent = visibleSeries.length > 0 || systemCurvePoints.length > 0;

	// A manually entered TDH skips the pipe details the system curve is built
	// from, so the operating point cannot be plotted against it.
	const isManualTdh = formData.tdhData.mode === "known";

	const dutyGpm = useMemo(() => {
		const value = parseNumericValue(gpmDisplayValue, NaN);
		return Number.isFinite(value) && value > 0 ? value : null;
	}, [gpmDisplayValue]);

	const dutyTdh = useMemo(() => {
		const value = parseNumericValue(tdhDisplayValue, NaN);
		return Number.isFinite(value) && value > 0 ? value : null;
	}, [tdhDisplayValue]);

	useEffect(() => {
		if (!chartData && !isResultsLoading && !resultsError) {
			void loadResults();
		}
	}, [chartData, resultsError, isResultsLoading, loadResults]);

	return (
		<div className="pf-step pf-step--pump-curve">

			{isResultsLoading && (
				<div
					className="pf-pump-curve-chart pf-pump-curve-chart--skeleton"
					aria-busy="true"
				>
					<LoadingIndicator message="Fetching results…" />
				</div>
			)}

			{resultsError && (
				<div className="pf-step__error" role="alert">
					<p>{resultsError}</p>
					<button
						type="button"
						className="pf-button pf-button--secondary"
						onClick={() => {
							void loadResults();
						}}
					>
						Retry
					</button>
				</div>
			)}

			{!isResultsLoading && chartData !== null ? (
				hasContent ? (
					<>
						<PumpCurveChart
							series={visibleSeries}
							systemCurvePoints={systemCurvePoints}
							dutyGpm={dutyGpm}
							dutyTdh={dutyTdh}
							hiddenSeries={hiddenSeries}
						/>

						{isManualTdh && (
							<Alert message="Operation point unavailable without full system curve" />
						)}

						{/* Both buttons only earn their place when both have curves
						    behind them; otherwise one would lead to an empty chart. */}
						{isSplitBySeriesType && availableSeriesTypes.size > 1 ? (
							<SeriesTypeToggle
								value={activeSeriesType}
								onChange={setSeriesType}
							/>
						) : null}
						<SeriesSelector series={visibleSeries} />
					</>
				) : (
					<div className="pf-pump-curve-placeholder" role="status">
						<p>No series data available to show for the selected criteria.</p>
					</div>
				)
			) : null}
		</div>
	);
}
